/**
 * The survey round email for the Settings preview: the same HTML the backend
 * sends (backend services/_survey_round_email_template.py), so the preview is
 * the email itself. Change both together.
 *
 * The body is plain text written in the app, escaped and never trusted as
 * HTML. Outlook draws emails with Word, so the layout is tables and inline
 * styles only.
 */

export const ANONYMITY_LINE = 'Your answers are anonymous: only your department is recorded with them.'

const PAGE = '#E6EFF8'
const NAVY = '#002677'
const BLUE = '#005EB8'
const CYAN = '#00AEEF'
const TINT = '#F3F8FD'
const INK = '#1E293B'
const MUTED = '#64748B'

export interface EmailPartner { team: string; lead: string; supports: string[] }

/** As Python's html.escape. */
const escape = (text: string) => text
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#x27;')

/** The name an email greets someone by. "Sam Taylor" is Sam; the directory's
 *  surname-first form, "PATEL Nayan", is Nayan. Blank when there is no name. */
export function firstName(name: string | null | undefined): string {
  const words = (name ?? '').split(/\s+/).filter(Boolean)
  const upper = (w: string) => w === w.toUpperCase() && w !== w.toLowerCase()
  if (words.length > 1 && upper(words[0]) && words[0].length > 1) {
    const rest = words.slice(1).filter(w => !upper(w))
    if (rest.length) return rest[0]
  }
  return words[0] ?? ''
}

function partnerItem(item: EmailPartner): string {
  const lead = item.lead ? `<span style="font-weight:normal;color:${MUTED};"> (${escape(item.lead)})</span>` : ''
  const supports = item.supports.length
    ? `<div style="margin:2px 0 0 0;font-size:13px;line-height:19px;color:${MUTED};">${escape(item.supports.join(', '))}</div>`
    : ''
  return '<tr><td style="padding:0 0 8px 0;">'
    + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>'
    + `<td style="background-color:${TINT};border-left:4px solid ${CYAN};border-radius:0 8px 8px 0;padding:12px 16px;">`
    + `<div style="font-size:15px;line-height:22px;font-weight:bold;color:${NAVY};">${escape(item.team)}${lead}</div>`
    + `${supports}</td></tr></table></td></tr>`
}

function bodyHtml(body: string, partner: string, partners: EmailPartner[]): string {
  const list = '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 16px 0;">'
    + partners.map(partnerItem).join('') + '</table>'
  const blocks: string[] = []
  for (const paragraph of body.replace(/\r\n/g, '\n').split('\n\n')) {
    if (!paragraph.trim()) continue
    const parts = paragraph.trim().split('{partners}')
    parts.forEach((part, i) => {
      if (part.trim()) {
        const lines = part.trim().split('\n').map(line => escape(line.replace(/\{partner\}/g, partner)))
        blocks.push(`<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:${INK};">${lines.join('<br>')}</p>`)
      }
      if (i < parts.length - 1 && partners.length) blocks.push(list)
    })
  }
  return blocks.join('\n')
}

export function surveyEmailHtml({ body, partner, partners, url, reminder, name, banner }: {
  body: string
  partner: string
  partners: EmailPartner[]
  url: string
  reminder: boolean
  name: string
  banner: string
}): string {
  const first = firstName(name)
  const greeting = first ? `Hi ${escape(first)},` : 'Hello,'
  // The line inboxes show beside the subject, before the email is opened,
  // instead of the banner's alt text.
  const preheader = reminder ? 'A reminder: if you have already answered, thank you.' : ANONYMITY_LINE
  const intro = reminder
    ? '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 24px 0;"><tr>'
      + '<td style="background-color:#FFF4E0;border-radius:8px;padding:12px 16px;font-size:14px;line-height:21px;color:#7A4B00;">'
      + '<strong>A reminder:</strong> if you have already answered, thank you, and there is nothing more to do.'
      + '</td></tr></table>'
    : ''
  return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:${PAGE};">
  <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:${PAGE};">${escape(preheader)}</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${PAGE};min-width:100%;">
    <tbody><tr><td style="padding:32px 10px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;background-color:#FFFFFF;border-collapse:separate;border-radius:12px;overflow:hidden;font-family:'Segoe UI',Aptos,Calibri,Arial,sans-serif;margin:0 auto;">
        <tbody>
          <tr>
            <td style="padding:0;background-color:${CYAN};">
              <img src="${escape(banner)}" width="600" alt="IT &amp; Data - North Europe. How did we do? Your feedback helps us improve" style="display:block;width:100%;max-width:600px;height:auto;border:0;">
            </td>
          </tr>
          <tr>
            <td style="padding:36px 40px 32px 40px;">
              ${intro}<h1 style="margin:0 0 20px 0;font-size:24px;line-height:32px;font-weight:bold;color:${BLUE};">${greeting}</h1>
              ${bodyHtml(body, partner, partners)}
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 12px 0;">
                <tbody><tr>
                  <td style="background-color:${BLUE};border-radius:6px;">
                    <a href="${escape(url)}" style="display:inline-block;padding:13px 32px;font-size:16px;line-height:20px;font-weight:bold;color:#FFFFFF;text-decoration:none;">Take the Survey</a>
                  </td>
                </tr></tbody>
              </table>
              <p style="margin:0 0 28px 0;font-size:13px;line-height:20px;color:${MUTED};">${escape(ANONYMITY_LINE)}</p>
              <p style="margin:0;font-size:15px;line-height:24px;color:${INK};">Best regards,<br><strong>IT &amp; Data NE - TrackIT</strong></p>
            </td>
          </tr>
        </tbody>
      </table>
      <p style="margin:16px auto 0 auto;max-width:600px;font-size:11px;line-height:16px;color:#7C8DA6;text-align:center;font-family:'Segoe UI',Aptos,Calibri,Arial,sans-serif;">This link is personal to you, so please don't forward this email.</p>
    </td></tr></tbody>
  </table>
</body>
</html>`
}
